"use client"

import React from "react"
import { useParams } from "react-router-dom"
import { LayoutDashboard, UtensilsCrossed, Users, ShoppingBag, Settings, BarChart3, TrendingUp, FileText, CreditCard, UserPlus, ChefHat, ConciergeBell } from "lucide-react"
import { useRestaurantAuth } from "../contexts/RestaurantAuthContext"
import { useLanguage } from "../contexts/LanguageContext"
import { useNewOrders } from "../contexts/NewOrderContext"
import { hasRoleAccess } from "../utils/permissions"
import UnifiedSidebar from "./ui/dashboard/UnifiedSidebar"
import TurboLogoIcon from "./TurboLogoIcon"

interface RestaurantSidebarProps {
  onClose?: () => void
}

const RestaurantSidebar: React.FC<RestaurantSidebarProps> = ({ onClose }) => {
  const { restaurantId } = useParams()
  const { logout, user, isOwner } = useRestaurantAuth()
  const { t } = useLanguage()
  const { newOrderCount } = useNewOrders()

  const userRole = user?.role || ''

  // Define all menu items with their route names for permission checking
  const allMenuItems = [
    {
      path: `/restaurant/${restaurantId}/dashboard`,
      icon: LayoutDashboard,
      label: t("dashboard"),
      routeName: "dashboard"
    },
    {
      path: `/restaurant/${restaurantId}/food-items`,
      icon: UtensilsCrossed,
      label: t("foodItems"),
      routeName: "food-items"
    },
    {
      path: `/restaurant/${restaurantId}/tables`,
      icon: Users,
      label: t("tables"),
      routeName: "tables"
    },
    {
      path: `/restaurant/${restaurantId}/orders`,
      icon: ShoppingBag,
      label: t("orders"),
      routeName: "orders",
      badge: newOrderCount
    },
    // Kitchen Staff Orders - only for kitchen staff
    {
      path: `/restaurant/${restaurantId}/kitchen-orders`,
      icon: ChefHat,
      label: t("kitchenOrders"),
      routeName: "kitchen-orders",
      badge: newOrderCount
    },
    // Waiter Orders - only for waiters
    {
      path: `/restaurant/${restaurantId}/waiter-orders`,
      icon: ConciergeBell,
      label: t("waiterOrders"),
      routeName: "waiter-orders"
    },
    // Analytics Section
    {
      path: `/restaurant/${restaurantId}/analytics`,
      icon: BarChart3,
      label: t("analytics"),
      routeName: "analytics"
    },
    {
      path: `/restaurant/${restaurantId}/food-items-performance`,
      icon: TrendingUp,
      label: t("foodPerformance"),
      routeName: "food-items-performance"
    },
    {
      path: `/restaurant/${restaurantId}/reports`,
      icon: FileText,
      label: t("reports"),
      routeName: "reports"
    },
    // POS Section
    {
      path: `/restaurant/${restaurantId}/pos`,
      icon: CreditCard,
      label: t("posSystem"),
      routeName: "pos"
    },
    {
      path: `/restaurant/${restaurantId}/delivery-orders`,
      icon: TurboLogoIcon,
      label: t("deliveryOrders"),
      routeName: "delivery-orders"
    },
    // Users Management Section
    {
      path: `/restaurant/${restaurantId}/users`,
      icon: UserPlus,
      label: t("staffManagement"),
      routeName: "users"
    },
    {
      path: `/restaurant/${restaurantId}/settings`,
      icon: Settings,
      label: t("settings"),
      routeName: "settings"
    },
  ]

  // Filter menu items based on role
  const menuItems = allMenuItems.filter((item) => {
    if (!item.routeName) return true
    return hasRoleAccess(item.routeName, userRole, isOwner)
  })

  return (
    <UnifiedSidebar
      menuItems={menuItems}
      title="Turbo Delivery"
      subtitle={t("restaurantPortal")}
      onLogout={logout}
      onClose={onClose}
      logoColor="#DC2626"
    />
  )
}

export default RestaurantSidebar 